import { useEffect, useState } from 'react';
import { Button, Field, Header, Message, Modal, Table, Toast, Input, Icon } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { InventoryItem, InventoryItemStatus, InventoryWarehouse, SetLoading } from '../SiteClasses';
import { bindClick } from '../SiteUtil';
import classNames from 'classnames';
import Validator from '../Validator';
import SearchCoreModal from '../components/SearchCoreModal';
import API from '../API';

interface Props{
	open: boolean,
	onClose: ()=>void,
	warehouse_id: number,
	onTransferred: (item_ids: number[])=>void,
	type_id?: number,
}

enum Modals{
	SEARCH_ITEMS = 12,
}

var TransferItemsModal = (props: Props)=>{
	var [warehouses, setWarehouses] = useState<InventoryWarehouse[]>(null);
	var [items, setItems] = useState<InventoryItem[]>([]);
	var [selectedItems, setSelectedItems] = useState<number[]>([]);
	var [targetWarehouse, setTargetWarehouse] = useState<number>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [lastSearch, setLastSearch] = useState<{ query: string, page: number, pageSize: number, response: any }>(null);
	var [reuseLastSearch, setReuseLastSearch] = useState<boolean>(false);

	useEffect(()=>{
		if(!props.open){
			setShownModal(null);
			return;
		}

		setItems([]);
		setSelectedItems([]);
		setTargetWarehouse(null);
		setModalPrompts(null);
		setLoadError(null);
		setWarehouses(null);
		setShownModal(null);
		setLastSearch(null);
		setReuseLastSearch(false);

		API.getWarehouses().then(res=>{
			if(res.error) return setLoadError('Hubo un error cargando los almacenes.');
			setWarehouses(res.data.filter(w=> w.warehouse_id !== props.warehouse_id));
		}).catch(()=> setLoadError('Hubo un error cargando los almacenes.'));
	}, [props.open]);

	var toggleItem = (item_id: number)=>{
		setSelectedItems(prev=>
			prev.includes(item_id) ? prev.filter(i=> i !== item_id) : [...prev, item_id]
		);
	};

	var allSelected = items.length > 0 && items.every(item=> selectedItems.includes(item.item_id));
	var addedItemIds = items.map(item=> item.item_id);

	var toggleAll = ()=>{
		setSelectedItems(allSelected ? [] : items.map(i=> i.item_id));
	};

	var searchAvailableItems = (query: string, page: number, pageSize: number)=>{
		var normalizedQuery = query || '';
		if(
			reuseLastSearch &&
			lastSearch &&
			lastSearch.query === normalizedQuery &&
			lastSearch.page === page &&
			lastSearch.pageSize === pageSize
		){
			setReuseLastSearch(false);
			return Promise.resolve({
				...lastSearch.response,
				data: lastSearch.response.data.filter((item: InventoryItem)=>
					item.status === InventoryItemStatus.AVAILABLE &&
					!addedItemIds.includes(item.item_id)
				),
			});
		}

		return API.searchItems(normalizedQuery, page * pageSize, pageSize, props.type_id, props.warehouse_id).then(res=>{
			if(res.error || !res.data) return res;
			setLastSearch({ query: normalizedQuery, page, pageSize, response: res });
			return {
				...res,
				data: res.data.filter(item=>
					item.status === InventoryItemStatus.AVAILABLE &&
					!addedItemIds.includes(item.item_id)
				),
			};
		});
	};

	var onSearchItemSelected = (item: InventoryItem, setLoading: SetLoading)=>{
		if(items.some(currentItem=> currentItem.item_id === item.item_id)){
			setLoading(false);
			return;
		}

		setReuseLastSearch(true);
		setSelectedItems(prev=> [...prev, item.item_id]);
		setItems(prev=> [...prev, item]);
		Toast.success('Equipo agregado a la lista para mover.');
		setLoading(false);
	};

	var onTransfer = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ targetWarehouse, selectedItems }, {
			targetWarehouse: [{ rule: 'empty', label: 'Almacén de destino' }],
			selectedItems: [{ rule: (v: number[])=> v && v.length > 0, prompt: 'Selecciona al menos un equipo.' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.transferInventoryItems(selectedItems, targetWarehouse).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Equipos transferidos correctamente.');
			props.onTransferred(selectedItems);
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado transfiriendo los equipos.']);
		}).finally(()=> setLoading(false));
	};

	return (
		<>
		<Modal open={props.open} onClose={props.onClose} size='small'>
			<Modal.Header>Mover equipos</Modal.Header>
			{loadError ? (
				<Modal.Content>
					<Header text='Error' subtext={loadError}/>
				</Modal.Content>
			) : !warehouses ? (
				<Modal.Content>
					<Header loading text='Cargando'/>
				</Modal.Content>
			) : (
				<Modal.Content style={{ maxHeight: 500, overflowY: 'auto', overflowX: 'hidden' }}>
					<Field label='Almacén destino' style={{ marginBottom: 12 }}>
						<Dropdown
							selection
							fluid
							placeholder='Selecciona almacén de destino...'
							value={targetWarehouse}
							onChange={(_, { value })=> setTargetWarehouse(value as number)}
							options={warehouses.map(w=>({ key: w.warehouse_id, value: w.warehouse_id, text: w.warehouse_name }))}
						/>
					</Field>
					<Field label='Equipos'>
						<Input
							placeholder='Buscar equipos...'
							readonly
							button={
								<Button
									text='Seleccionar'
									onClick={bindClick(setShownModal, Modals.SEARCH_ITEMS)}
								/>
							}
						/>
					</Field>
					{items.length === 0 && <Header style={{ marginTop: 20 }} text='Sin equipos' subtext='Agrega equipos para mover.'/>}
					{items.length !== 0 && <>
					<div style={{ textAlign: 'center' }}>
						<Button text={allSelected ? 'Deseleccionar todos' : 'Seleccionar todos'} color='black' size='tiny' onClick={toggleAll}/>
					</div>
					<Table
						selectable
						fitted
						style={{ marginTop: 15 }}
						collapsingIndexes={[0, 1, 3, 4]}
						centeredIndexes={[0]}
						headers={['', 'ID', 'Nombre', 'S/N', 'Modelo']}
						onClick={(v: any[])=>{
							var id = v[1] as number;
							toggleItem(id);
						}}
						data={items.map(item=>{
							var selected = selectedItems.includes(item.item_id);
							return [
								<i className={classNames('large icon circle', { check: selected, outline: !selected })}/>,
								item.item_id,
								item.item_name || '—',
								item.serial_number || '—',
								item.model || '—',
							];
						})}
					/></>}
					<Message style={{ marginTop: 15 }} type='error' list={modalPrompts}/>
				</Modal.Content>
			)}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose}/>
				<Button color='black' text={`Mover${selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}`} onClick={onTransfer} disabled={selectedItems.length === 0}/>
			</Modal.Actions>
		</Modal>
		<SearchCoreModal
			open={shownModal === Modals.SEARCH_ITEMS}
			onClose={bindClick(setShownModal, null)}
			title='Buscar equipos'
			searchPlaceholder='Buscar por ID, nombre, serie, modelo o tipo'
			selectedFilter={addedItemIds.join(',')}
			onShowLoad={searchAvailableItems}
			onSelected={onSearchItemSelected}
			searchData={searchAvailableItems}
			pageSize={10}
			table={[
				{ name: 'ID', key: 'item_id', collapsing: true },
				{
					name: 'Equipo',
					key: 'type_name',
					render: (val, item: InventoryItem) => (<><Icon name={item.type_icon || 'box-open'} /> {val || `Equipo ${item.type_id}`}</>),
				},
				{ name: 'Nombre', key: 'item_name' },
				{ name: 'Serie', key: 'serial_number' },
				{ name: 'Modelo', key: 'model' },
			]}
		/>
		</>
	);
};

export default TransferItemsModal;
